import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SEO from './components/SEO';
import Home from './pages/Home';
import Services from './pages/Services';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import About from './pages/About';
import PageHeader from './components/PageHeader';
import WhatsAppFloat from './components/WhatsAppFloat';

import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';

function AnimatedMain({ children }) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return (
    <main className="page-enter" key={pathname}>
      {children}
    </main>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <Router>
          <div className="App">
        {/* SEO pour la page d'accueil */}
        <SEO
          title="Sénégalaise Déco Menuiserie - Meubles sur mesure à Mbour, Sénégal"
          description="Atelier de menuiserie à Mbour, à 1 km sur la route de Joal. Fabrication de meubles sur mesure en bois massif. Qualité et savoir-faire artisanal sénégalais."
          keywords="menuiserie, meubles sur mesure, sénégal, dakar, mbour, joal, menuisier, ébénisterie, meubles en bois, artisanat sénégalais"
        />

        <Navbar />
        <AnimatedMain>
          <Routes>
            <Route path="/" element={
              <>
                <SEO 
                  title="Sénégalaise Déco Menuiserie - Meubles sur mesure à Mbour, Sénégal"
                  description="Atelier de menuiserie à Mbour, à 1 km sur la route de Joal. Fabrication de meubles sur mesure en bois massif. Qualité et savoir-faire artisanal sénégalais."
                  keywords="menuiserie, meubles sur mesure, sénégal, dakar, mbour, joal, menuisier, ébénisterie, meubles en bois, artisanat sénégalais"
                />
                <Home />
              </>
            } />
            
            <Route path="/services" element={
              <>
                <SEO 
                  title="Nos Services de Menuiserie - Sénégalaise Déco Menuiserie"
                  description="Découvrez nos services de menuiserie sur mesure au Sénégal. Création, rénovation et aménagement de meubles en bois massif par des artisans qualifiés."
                  keywords="services menuiserie, fabrication meubles sur mesure, rénovation meubles, aménagement intérieur, ébénisterie sénégalaise, atelier menuiserie"
                />
                <Services />
              </>
            } />
            
            <Route path="/gallery" element={
              <>
                <SEO 
                  title="Galerie de Réalisations - Sénégalaise Déco Menuiserie"
                  description="Parcourez notre galerie de réalisations en menuiserie. Découvrez nos créations uniques de meubles sur mesure fabriqués à la main au Sénégal."
                  keywords="galerie menuiserie, réalisations meubles, photos meubles sur mesure, créations menuiserie sénégalaise, exemples de travaux"
                />
                <Gallery />
              </>
            } />
            
            <Route path="/contact" element={
              <>
                <SEO 
                  title="Contactez-nous - Sénégalaise Déco Menuiserie"
                  description="Contactez Sénégalaise Déco Menuiserie pour vos projets de meubles sur mesure. Notre atelier est situé à Mbour, à 1 km sur la route de Joal."
                  keywords="contact menuiserie sénégal, devis meuble sur mesure, adresse atelier, téléphone menuisier, email contact"
                />
                <Contact />
              </>
            } />
            
            <Route path="/about" element={
              <>
                <SEO 
                  title="À Propos de Sénégalaise Déco Menuiserie"
                  description="Découvrez l'histoire et les valeurs de Sénégalaise Déco Menuiserie, votre atelier de menuiserie artisanale à Mbour, sur la route de Joal."
                  keywords="à propos, histoire, valeurs, équipe, savoir-faire, menuiserie artisanale, ébénisterie sénégalaise"
                />
                <About />
              </>
            } />
            
            {/* Page de menuiserie optimisée pour le référencement */}
            <Route path="/menuiserie-senegal" element={
              <div className="menuiserie-page">
                <SEO 
                  title="Menuiserie Sénégal - Fabrication de Meubles sur Mesure à Mbour"
                  description="Sénégalaise Déco Menuiserie, votre expert en menuiserie au Sénégal. Création de meubles sur mesure en bois massif à Mbour, sur la route de Joal. Qualité et savoir-faire 100% sénégalais."
                  keywords="menuiserie sénégal, meubles sur mesure sénégal, ébénisterie joal-fadiouth, fabrication meubles dakar, atelier menuiserie mbour, artisanat bois sénégalais"
                />
                <PageHeader
                  image="/img/gallery3.webp"
                  eyebrow="Mbour · Petite Côte"
                  title="Menuiserie Sénégal - Création de Meubles sur Mesure"
                />

                <section className="section container prose">
                  <h2>Votre Atelier de Menuiserie à Mbour</h2>
                  <p>Bienvenue chez Sénégalaise Déco Menuiserie, votre atelier de menuiserie de référence à Mbour, à seulement 1 km sur la route de Joal. Spécialisés dans la création de meubles sur mesure en bois massif, nous mettons notre savoir-faire artisanal à votre service pour réaliser vos projets d'aménagement intérieur.</p>
                  
                  <h3>Nos Services de Menuiserie</h3>
                  <ul>
                    <li>Fabrication de meubles sur mesure (cuisine, salle de bain, chambre, salon, bureau)</li>
                    <li>Agencement d'intérieur sur-mesure</li>
                    <li>Restauration de meubles anciens</li>
                    <li>Création de meubles en bois massif</li>
                    <li>Décoration intérieure</li>
                  </ul>
                  
                  <h3>Notre Savoir-Faire Artisanal</h3>
                  <p>Notre atelier situé à Mbour réunit des artisans menuisiers expérimentés, héritiers d'un savoir-faire traditionnel sénégalais. Chaque pièce est fabriquée à la main avec soin et précision, en utilisant des essences de bois locales de qualité supérieure.</p>
                  
                  <h3>Pourquoi Choisir Notre Atelier de Menuiserie ?</h3>
                  <ul>
                    <li>Expertise locale avec plus de 10 ans d'expérience</li>
                    <li>Matériaux de qualité, principalement du bois massif local</li>
                    <li>Créations uniques et personnalisées</li>
                    <li>Prix compétitifs et devis personnalisés</li>
                    <li>Service client personnalisé et suivi de projet</li>
                  </ul>
                  
                  <h3>Zone d'Intervention</h3>
                  <p>Notre atelier est basé à Joal-Fadiouth, mais nous intervenons dans tout le Sénégal, notamment à Mbour, Dakar, Thiès et leurs environs. N'hésitez pas à nous contacter pour discuter de votre projet de menuiserie ou pour obtenir un devis personnalisé.</p>
                </section>
                
                <section className="section section-alt cta-band">
                  <div className="container">
                    <h2>Un Projet de Meuble sur Mesure ?</h2>
                    <p>Contactez-nous dès aujourd'hui pour discuter de votre projet. Notre équipe d'experts est à votre écoute pour concrétiser vos idées en meubles uniques et fonctionnels.</p>
                    <a href="/contact" className="btn btn-primary">Demander un Devis</a>
                  </div>
                </section>
              </div>
            } />
          </Routes>
        </AnimatedMain>
        <Footer />
        <WhatsAppFloat />
      </div>
    </Router>
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
